import { CHUNK_SIZE, isUploadVerificationPending, uploadObjectControlColumns, type UploadObjectControlRow } from "@uploads";
import { readCurrentAdminUploadScope } from "./adminUploadAuthScope";
import {
	computeRetryAt,
	inspectPersistedChunkIntegrity,
	isBrowserOffline,
	isOfflineUploadFailure,
	mergeAndPersistSessionUrls,
	resolveUploadTargetAdapter,
	isUploadsUploadTarget,
	chunkedObjectLaneColumns,
	createUploadsMultipartUploadTarget,
	persistedUploadTarget,
	type FamilyAwareMultipartUploadTarget,
	type MultipartUrlRefreshResult,
	type MultipartUrlRefreshTargetMigration,
	type NewSessionInput,
	type NeedsAttentionOutcome,
	type SessionStore,
	type TargetAttachmentResult,
	type VerifyReinitializableResult
} from "@uploads";
import { adminUploadTelemetry } from "@/services/uploads/adminUploadTelemetry";
import type { AdminVideoTarget } from "@/services/uploads/adminUploadTargetInitiator";
import * as fileUploadDb from "@/db/fileUploadDb";
import type { FileUploadChunkMeta, FileUploadSession } from "@/db/fileUploadDb";
import { ensureAdminUploadBridgeReady } from "./adminUploadMigrationIntegration";
import { claimAdminFreshSessionLease, getAdminDurableOwnershipForCoordinator } from "./adminUploadLease";
import { redactUploadLogText, uploadFailureLogDetails } from "./uploadLogRedaction";
import { PENDING_UPLOAD_TARGET_PREFIX } from "./pendingUploadTarget";

interface AdminSessionStore extends SessionStore<FileUploadSession, FileUploadChunkMeta, ArrayBuffer> {
	attachTarget(
		session: FileUploadSession,
		target: FamilyAwareMultipartUploadTarget,
		retainOwner?: boolean
	): Promise<TargetAttachmentResult<FileUploadSession>>;
	markRecoveryBlocked(session: FileUploadSession, reason: string): Promise<NeedsAttentionOutcome | "already-needs-attention" | void>;
	mergeAndPersistUrls(
		session: FileUploadSession,
		updates: readonly MultipartUrlRefreshResult[],
		fallbackUrls: readonly string[],
		refreshesAllPendingUrls?: boolean,
		targetMigration?: MultipartUrlRefreshTargetMigration
	): Promise<string[]>;
	cleanupCompleted(session: FileUploadSession): Promise<void>;
	findByUploadId(uploadId: string): Promise<FileUploadSession | undefined>;
	getById(sessionId: string): Promise<FileUploadSession | undefined>;
	createSession(input: NewSessionInput, leaseToken: unknown): Promise<{ session: FileUploadSession; created: boolean }>;
	persistChunk(session: FileUploadSession, chunk: { chunkIndex: number; size: number; data: ArrayBuffer }): Promise<void>;
	finishPrePersist(session: FileUploadSession): Promise<void>;
	abandonSetup(session: FileUploadSession): Promise<void>;
	deleteAllChunkData(session: FileUploadSession): Promise<void>;
	tombstoneCancelled(session: FileUploadSession, options?: { retainChunks?: boolean; notifyOtherTabs?: boolean }): Promise<void>;
	markChunkUploaded(session: FileUploadSession, chunkIndex: number, etag: string): Promise<void>;
	/** Internal adapter seam: only invoke from inside the established durable fence. */
	markChunkUploadedAlreadyFenced(session: FileUploadSession, chunkIndex: number, etag: string): Promise<void>;
	deleteSessionByIdIfUploadIdMatches(
		sessionId: string,
		uploadId: string,
		// The identity the CALLER captured when it owned this row. Without it the delete can only CAS on
		// values read from the row itself, which is no CAS at all against a successor claim.
		expectedIdentity?: { uploadOwnerId?: string; uploadOwnerClaimId?: string }
	): Promise<void>;
	verifyReinitializableWithRetainedFile(
		session: FileUploadSession | undefined,
		retainedFile?: File
	): Promise<VerifyReinitializableResult>;
	resetForReinitialization(session: FileUploadSession, target: FamilyAwareMultipartUploadTarget): Promise<FileUploadSession>;
}

/**
 * The thirteen durable columns a multipart target owns, projected off the target itself.
 * `createSession` builds the same thirteen inline for a brand-new row; `resetForReinitialization`
 * reuses this projection so the two target writes cannot drift on what "this row points at that
 * upload" means.
 *
 * `uploadId` deliberately mirrors an `/uploads` target's `registryId`: `uploadTargetOpaqueId` IS the
 * registryId for that family, and the shared driver resolves persisted rows through it.
 */
function targetPersistenceFields(
	target: FamilyAwareMultipartUploadTarget,
	// The row being overwritten, when there is one. Only the chunked-object lane columns read it: a
	// re-mint that carries no lane block must not blank the slicing facts the row still depends on.
	existing?: Pick<FileUploadSession, "chunkSizeBytes" | "priorityChunkIndexesJson"> & UploadObjectControlRow
): fileUploadDb.FileUploadSessionTargetFields {
	const targetAdapter = resolveUploadTargetAdapter(target);
	return {
		...uploadObjectControlColumns(isUploadsUploadTarget(target) ? target : undefined, existing),
		uploadId: targetAdapter.opaqueId(target),
		s3Key: isUploadsUploadTarget(target) ? "" : target.s3Key,
		bucket: isUploadsUploadTarget(target) ? "" : target.bucket,
		chunkUrlsJson: JSON.stringify(targetAdapter.chunkUrls(target)),
		endpointFamily: targetAdapter.family,
		registryId: isUploadsUploadTarget(target) ? target.registryId : undefined,
		attemptId: isUploadsUploadTarget(target) ? target.attemptId : undefined,
		generation: isUploadsUploadTarget(target) ? target.generation : undefined,
		takeGeneration: isUploadsUploadTarget(target) ? target.takeGeneration : undefined,
		targetEnvelopesJson: isUploadsUploadTarget(target) ? JSON.stringify(target.envelopes) : undefined,
		// Plan 3a (LSP-17653): the three chunked-object lane columns, projected by the ONE rule every
		// writer of them shares across both web apps (`createSession` is the other one here). A
		// multipart target leaves all three `undefined`, so its row stays byte-identical to today's.
		...chunkedObjectLaneColumns(isUploadsUploadTarget(target) ? (target as unknown as AdminVideoTarget) : undefined, existing)
	};
}

/**
 * One line per retained-byte fail condition, naming the condition and the counts behind it. This is
 * the only place that can still say WHICH buffers were missing: by the time the driver reaches its
 * terminal bytes-gone verdict the row (and, in admin, the chunk grid) is already being deleted.
 */
function warnRetainedBytesMissing(
	session: Pick<FileUploadSession, "id" | "uploadId" | "totalChunks" | "fileSize">,
	integrity: { reason?: string; presentBufferCount?: number } & Record<string, unknown>
): void {
	try {
		console.warn("[Upload] Retained upload bytes are not reinitializable", {
			app: "admin",
			sessionId: session.id,
			uploadId: session.uploadId,
			reason: integrity.reason,
			presentBufferCount: integrity.presentBufferCount,
			totalChunks: session.totalChunks,
			fileSize: session.fileSize,
			...(typeof integrity.chunkIndex === "number" ? { chunkIndex: integrity.chunkIndex } : {}),
			...(typeof integrity.expectedByteLength === "number" ? { expectedByteLength: integrity.expectedByteLength } : {}),
			...(typeof integrity.actualByteLength === "number" ? { actualByteLength: integrity.actualByteLength } : {}),
			...(typeof integrity.actualByteTotal === "number" ? { actualByteTotal: integrity.actualByteTotal } : {})
		});
	} catch {
		// A diagnostic failure must not change the verification verdict.
	}
}

function assertUploadSessionCasApplied(applied: boolean, operation: string, session: FileUploadSession): void {
	if (applied) return;
	const context = { sessionId: session.id, uploadId: session.uploadId };
	try {
		console.warn(`[Upload] ${operation} CAS mismatch`, context);
	} catch {
		// A diagnostic failure must not hide the persistence CAS failure.
	}
	throw new Error(`[Upload] ${operation} CAS mismatch for session ${session.id}, upload ${session.uploadId}`);
}

function withSessionDefaults(session: FileUploadSession | undefined): FileUploadSession | undefined {
	if (!session) return session;
	if (session.sourceReadStrategy !== undefined && session.endpointFamily !== undefined) return session;
	return {
		...session,
		sourceReadStrategy: session.sourceReadStrategy ?? "slice",
		// Pre-WO-10 rows are pinned legacy once observed; a runtime rollback can never
		// re-route them into the `/uploads` family.
		endpointFamily: session.endpointFamily ?? "legacy"
	};
}

function warnNeedsAttentionReason(session: Pick<FileUploadSession, "id" | "uploadId" | "conversationId">, reason?: string): void {
	const context = { app: "admin", sessionId: session.id, uploadId: session.uploadId, conversationId: session.conversationId };
	try {
		if (reason === undefined) {
			console.warn("[Upload] Marking session needs-attention with no reason supplied", context);
			return;
		}
		console.warn("[Upload] Marking session needs-attention", {
			...context,
			reason: redactUploadLogText(fileUploadDb.truncateUploadFailureReason(reason))
		});
	} catch {
		// A diagnostic failure must not block persistence.
	}
}

async function publishAdminUploadWork(): Promise<void> {
	try {
		const ownership = await getAdminDurableOwnershipForCoordinator();
		await ownership?.noteWork({ originKey: "contact-admin", pendingCount: 1, hasDueWork: true });
	} catch (error) {
		// Work publication is a wake-up hint; the durable session remains authoritative.
		adminUploadTelemetry.logWarn("recording.fileUpload.workPublicationFailed", { originKey: "contact-admin" }, error);
	}
}

function warnNeedsAttentionCasDecline(
	session: Pick<FileUploadSession, "id" | "uploadId" | "conversationId">,
	outcome: Extract<fileUploadDb.FileUploadSessionStatusUpdateOutcome, { applied: false }>
): void {
	try {
		// One line per decline reason: this transition is now a soft no-op rather than a throw, and a
		// silent no-op on a terminal write is exactly the shape that costs hours to reconstruct later.
		console.warn("[Upload] needs-attention CAS declined — reporting the winning state instead", {
			app: "admin",
			sessionId: session.id,
			uploadId: session.uploadId,
			conversationId: session.conversationId,
			reason: outcome.identityMatched ? "terminal-precedence" : (outcome.identityRefusalReason ?? "identity-mismatch"),
			currentStatus: outcome.currentStatus ?? "<row gone>"
		});
	} catch {
		// A diagnostic failure must not change the persistence verdict.
	}
}

export function createAdminSessionStore(): AdminSessionStore {
	return {
		getById: async (sessionId) =>
			withSessionDefaults(await fileUploadDb.withIdbReopen(() => fileUploadDb.readFileUploadSession(sessionId))),
		async createSession(input, leaseToken) {
			const uploadScope = input.uploadScope ?? readCurrentAdminUploadScope();
			// P-11: direct foreground ingress does not wait for the background
			// drain. Refuse rather than write an unfenced row when bridge admission
			// cannot be established.
			await ensureAdminUploadBridgeReady();
			if (!input.target && input.phase !== "deferred") throw new Error("Admin persistence requires a multipart upload target");
			const now = Date.now();
			const candidate: FileUploadSession = {
				id: input.id,
				batchId: input.batchId,
				batchOrganizationId: uploadScope?.organizationId ?? undefined,
				batchUserId: uploadScope?.userId ?? undefined,
				...(input.phase === "deferred" && input.batchId ? { retainBatchHistory: true } : {}),
				conversationId: input.conversationId,
				fileName: input.fileName,
				fileSize: input.fileSize,
				fileType: input.fileType,
				uploadMethod: "multipart",
				fileCategory: input.fileCategory,
				...(input.target
					? targetPersistenceFields(input.target)
					: {
							uploadId: `${PENDING_UPLOAD_TARGET_PREFIX}${input.id}`,
							s3Key: "",
							bucket: "",
							chunkUrlsJson: "[]",
							endpointFamily: "uploads" as const,
							targetPending: true,
							protocolSelectionPending: true,
							chunkSizeBytes: CHUNK_SIZE
						}),
				totalChunks: input.totalChunks,
				urlCreatedAt: input.target ? now : 0,
				status: input.phase === "direct-upload" ? "uploading" : "queued",
				createdAt: now,
				cloudHosted: input.cloudHosted,
				sourceReadStrategy: input.sourceReadStrategy ?? "slice",
				uploadEngineFamily: "legacy",
				uploadSchemaVersion: 2,
				uploadAdapterContractVersion: 1,
				uploadAdoptionState: "legacy",
				projectChecklistItemId: input.projectChecklistItemId,
				photoTime: input.photoTime,
				// LSP-18592: no key at all when capture evidence is off, so the row is byte-identical to develop's.
				...(input.captureEvidence ? { captureEvidence: input.captureEvidence } : {}),
				...fileUploadDb.getFileUploadSessionOwnerFields(
					fileUploadDb.getCurrentFileUploadSessionOwnerId(),
					leaseToken as string | undefined
				)
			};
			const result = await fileUploadDb.createFileUploadSessionWithOutcome(candidate);
			// A newly inserted row is already stamped with this scope's row-level owner identity,
			// but production ownership also requires a durable owner/epoch. Acquire that epoch
			// before returning the row to the shared driver, whose very first chunk checkpoint
			// deliberately fails closed when the durable writer is absent. Existing rows are
			// claimed by the driver's normal resume/direct path and must not be re-claimed here.
			if (result.created) await claimAdminFreshSessionLease(result.session);
			if (result.created && candidate.status === "queued") await publishAdminUploadWork();
			return result;
		},
		persistChunk: (session, chunk) =>
			fileUploadDb.saveFileUploadChunk(
				{
					sessionId: session.id,
					chunkIndex: chunk.chunkIndex,
					buffer: chunk.data,
					isUploaded: 0,
					etag: null,
					size: chunk.size
				},
				{
					uploadOwnerId: session.uploadOwnerId,
					uploadId: session.uploadId,
					uploadOwnerClaimId: session.uploadOwnerClaimId
				}
			),
		async finishPrePersist(session) {
			if (session.targetPending) {
				// Invoked inside the driver's existing exact-writer transaction after all chunks land.
				await fileUploadDb.updateFileUploadSession(session.id, { sourcePrepared: true });
				session.sourcePrepared = true;
			}
			// Pre-persist is a producer-to-drain handoff, not a retry. Clear the app-row owner
			// while the shared driver still holds the exact durable writer fence; release() then
			// drops that same epoch, leaving one immediately drainable pending row.
			//
			// LSP-19537: stamp this row with the CURRENT PAGE LOAD's lane id, so a foreign tab's
			// discoverPending can tell "a live lane elsewhere still intends this row" from "a genuine
			// orphan" instead of treating every ownerless pending row as fair game.
			const laneId = fileUploadDb.getCurrentFileUploadLaneId();
			const laneIntentAt = Date.now();
			const released = await fileUploadDb.markFileUploadSessionPendingForResume(
				session.id,
				{
					uploadId: session.uploadId,
					uploadOwnerId: session.uploadOwnerId,
					uploadOwnerClaimId: session.uploadOwnerClaimId
				},
				{ retryAt: null, retryAttempt: session.retryAttempt ?? 0, laneIntent: { laneId, at: laneIntentAt } }
			);
			assertUploadSessionCasApplied(released, "pre-persist handoff", session);
			Object.assign(session, {
				status: "pending",
				retryAt: null,
				retryAttempt: session.retryAttempt ?? 0,
				uploadOwnerId: undefined,
				uploadOwnerHeartbeatAt: undefined,
				uploadOwnerClaimId: undefined,
				laneIntentId: laneId,
				laneIntentAt
			});
		},
		abandonSetup: (session) =>
			fileUploadDb
				.deleteFileUploadSession(session.id, session.uploadId, session.uploadOwnerId, session.uploadOwnerClaimId, {
					requireExactOwnerIdentity: true,
					notifyOtherTabs: false
				})
				.then(() => undefined),
		deleteAllChunkData: (session) =>
			fileUploadDb.deleteChunkDataForSession(session.id, {
				uploadId: session.uploadId,
				uploadOwnerId: session.uploadOwnerId,
				uploadOwnerClaimId: session.uploadOwnerClaimId
			}),
		async mergeAndPersistUrls(session, updates, fallbackUrls, refreshesAllPendingUrls, targetMigration) {
			return fileUploadDb.withIdbReopen(() =>
				fileUploadDb.db.transaction("rw", fileUploadDb.fileUploadSessionControlTables, async () => {
					return mergeAndPersistSessionUrls(
						async () => {
							const currentSession = await fileUploadDb.readFileUploadSession(session.id);
							// extended architecture campaign: uploadId can still match after completion/cancellation.
							if (
								currentSession?.status === "complete" ||
								currentSession?.status === "cancelled" ||
								currentSession?.status === "needs-attention" ||
								currentSession?.status === "recovery-blocked"
							)
								return undefined;
							return currentSession;
						},
						async (changes) => {
							const currentSession = await fileUploadDb.readFileUploadSession(session.id);
							let refreshedEnvelopesJson: string | undefined;
							if (session.endpointFamily === "uploads") {
								if (!currentSession) throw new Error("Cannot refresh a missing uploads-family session");
								if (targetMigration) {
									const replacement = createUploadsMultipartUploadTarget(targetMigration.replacementTarget);
									if (
										replacement.registryId !== currentSession.registryId ||
										replacement.attemptId !== session.attemptId ||
										replacement.generation !== session.generation ||
										replacement.envelopes.length !== session.totalChunks
									) {
										throw new Error("Uploads URL refresh migration does not match the pinned session");
									}
									refreshedEnvelopesJson = JSON.stringify(replacement.envelopes);
									return fileUploadDb.updateFileUploadSession(session.id, {
										...changes,
										attemptId: replacement.attemptId,
										generation: replacement.generation,
										takeGeneration: replacement.takeGeneration,
										targetEnvelopesJson: refreshedEnvelopesJson,
										// The row now points at a DIFFERENT attempt, so it must adopt that attempt's lane
										// too — the same ONE projection rule `targetPersistenceFields` and `createSession`
										// use. Leaving the previous attempt's `transport` in place would keep a migrated
										// row claiming the chunked-object lane while the live attempt is the multipart one
										// the announcement gate verified, and `/complete` would send a manifest body the
										// server answers for a parts body. No `existing` is passed: a migration replaces
										// the slicing facts outright rather than re-normalizing the ones already there.
										...chunkedObjectLaneColumns(replacement as unknown as { transport?: string })
									});
								}
								// Rebuilt through `persistedUploadTarget`, never a bare
								// `createUploadsMultipartUploadTarget`: every rebuild from `targetEnvelopesJson` has to
								// carry the row's minted `transport`, or a resumed chunked-object attempt is
								// re-normalized into a multipart one and completes with the wrong body.
								try {
									JSON.parse(currentSession?.targetEnvelopesJson ?? "");
								} catch {
									throw new Error("Cannot refresh an uploads-family session with invalid persisted envelopes");
								}
								// Only the PARSE is guarded above: `persistedUploadTarget`'s own validation refusals
								// (a cross-generation envelope, a mismatched fence) must reach the caller with their
								// own message, not be relabelled as invalid persisted envelopes.
								const current = persistedUploadTarget({
									...currentSession,
									endpointFamily: "uploads",
									registryId: session.registryId ?? "",
									attemptId: session.attemptId ?? "",
									generation: session.generation ?? 0,
									takeGeneration: session.takeGeneration ?? "",
									targetEnvelopesJson: currentSession.targetEnvelopesJson,
									transport: session.transport
								});
								// `persistedUploadTarget` answers undefined only for a non-`uploads` family row.
								// A silent `!` here would crash further down with a bare "cannot read envelopes";
								// name the row instead so the log identifies which session was mis-familied.
								if (!current) {
									throw new Error(
										`Cannot refresh uploads session ${session.registryId ?? "(no registryId)"}: persisted row is not an uploads-family target`
									);
								}
								const refreshedByOrdinal = new Map(updates.map((update) => [update.partNumber, update.envelope]));
								const updatesByOrdinal = new Map(updates.map((update) => [update.partNumber, update]));
								const envelopes = current.envelopes.map((existing) => {
									const refreshed = refreshedByOrdinal.get(existing.ordinal);
									if (!refreshed) return existing;
									if (
										refreshed.ordinal !== existing.ordinal ||
										refreshed.targetId !== existing.targetId ||
										refreshed.attemptId !== current.attemptId ||
										refreshed.generation !== current.generation ||
										refreshed.url !== updatesByOrdinal.get(existing.ordinal)?.url
									)
										throw new Error("Uploads URL refresh target fence does not match the pinned session");
									return refreshed;
								});
								if (updates.some((update) => !update.envelope))
									throw new Error("Uploads URL refresh omitted a signed target envelope");
								refreshedEnvelopesJson = JSON.stringify(
									createUploadsMultipartUploadTarget({ ...current, envelopes }).envelopes
								);
							}
							return fileUploadDb.updateFileUploadSession(session.id, {
								...changes,
								...(refreshedEnvelopesJson ? { targetEnvelopesJson: refreshedEnvelopesJson } : {})
							});
						},
						session,
						updates,
						fallbackUrls,
						adminUploadTelemetry,
						(currentSession, expectedSession) => {
							// The shared caller collapses every false into one "session-identity-mismatch" reason, so a
							// corrupt-owner refusal would be indistinguishable from an ordinary competing-tab race in
							// telemetry. Name it here, where the distinction is still known.
							const currentOwnerIsUsable =
								currentSession.uploadOwnerId === undefined ||
								fileUploadDb.isValidFileUploadOwnerId(currentSession.uploadOwnerId);
							const expectedOwnerIsUsable =
								expectedSession.uploadOwnerId === undefined ||
								fileUploadDb.isValidFileUploadOwnerId(expectedSession.uploadOwnerId);
							if (!currentOwnerIsUsable || !expectedOwnerIsUsable) {
								try {
									console.warn("[Upload] URL merge refused — invalid owner identity", {
										sessionId: session.id,
										uploadId: session.uploadId,
										side: !expectedOwnerIsUsable ? "expected" : "persisted",
										ownerIdLength: fileUploadDb.getFileUploadOwnerIdLength(
											!expectedOwnerIsUsable ? expectedSession.uploadOwnerId : currentSession.uploadOwnerId
										)
									});
								} catch {
									// A diagnostic failure must not change the refusal.
								}
								return false;
							}
							const baseIdentityMatches =
								currentSession.uploadId === expectedSession.uploadId &&
								currentSession.uploadOwnerId === expectedSession.uploadOwnerId &&
								currentSession.uploadOwnerClaimId === expectedSession.uploadOwnerClaimId;
							if (!baseIdentityMatches) {
								if (targetMigration) {
									throw new Error("Uploads URL refresh migration lost durable session ownership");
								}
								return false;
							}
							if (!targetMigration) return true;
							const currentIsPrevious =
								currentSession.attemptId === targetMigration.previousAttemptId &&
								currentSession.generation === targetMigration.previousGeneration;
							const currentIsReplacement =
								currentSession.attemptId === targetMigration.replacementTarget.attemptId &&
								currentSession.generation === targetMigration.replacementTarget.generation;
							if (!currentIsPrevious && !currentIsReplacement) {
								throw new Error("Uploads URL refresh migration lost its durable attempt fence");
							}
							return currentIsPrevious;
						},
						refreshesAllPendingUrls || targetMigration !== undefined
					);
				})
			);
		},
		async releasePending(session, reason, options) {
			const verificationReason = isUploadVerificationPending(reason) ? reason : undefined;
			const retryAttempt = options?.retryAttempt ?? (session.retryAttempt ?? 0) + 1;
			const retryAt =
				options && "retryAt" in options ? options.retryAt : computeRetryAt({ attempt: retryAttempt - 1, nowMs: Date.now() });
			const released = await fileUploadDb.markFileUploadSessionPendingForResume(
				session.id,
				{
					uploadId: session.uploadId,
					uploadOwnerId: session.uploadOwnerId,
					uploadOwnerClaimId: session.uploadOwnerClaimId
				},
				{ retryAt, retryAttempt, ...(verificationReason ? { reason: verificationReason } : {}) }
			);
			if (!released) {
				throw new Error(
					`Upload session ${session.id} no longer exists or no longer owns the persisted upload; cannot defer for resume` +
						(reason ? ` after ${reason}` : "")
				);
			}
			Object.assign(session, {
				status: "pending",
				lastError: verificationReason,
				retryAt,
				retryAttempt,
				uploadOwnerId: undefined,
				uploadOwnerHeartbeatAt: undefined,
				uploadOwnerClaimId: undefined
			});
		},
		async markTerminalComplete(session) {
			const applied = await fileUploadDb.markFileUploadSessionComplete(
				session.id,
				session.uploadId,
				session.uploadOwnerId,
				session.uploadOwnerClaimId
			);
			assertUploadSessionCasApplied(applied, "terminal completion", session);
		},
		async cleanupCompleted(session) {
			const applied = await fileUploadDb.cleanupCompletedFileUploadSession(
				session.id,
				session.uploadId,
				session.uploadOwnerId,
				session.uploadOwnerClaimId
			);
			assertUploadSessionCasApplied(applied, "completed-session cleanup", session);
		},
		async markBytesGone(session, reason) {
			// Admin has no durable "bytes-gone" status — this cleanup DELETES the row and its chunk grid,
			// so this warn is the last chance to record why the only local copy was declared unusable.
			// R-1 keeps this discard-style terminal for exactly one case: the retained buffers really are
			// missing or incomplete, which `verifyReinitializable` has already named condition by
			// condition above.
			try {
				console.warn("[Upload] Discarding an upload whose local bytes are gone", {
					app: "admin",
					sessionId: session.id,
					uploadId: session.uploadId,
					conversationId: session.conversationId,
					totalChunks: session.totalChunks,
					reason: redactUploadLogText(fileUploadDb.truncateUploadFailureReason(reason))
				});
			} catch {
				// A diagnostic failure must not block the terminal cleanup.
			}
			const applied = await fileUploadDb.completeFileUploadSession(
				session.id,
				session.uploadId,
				session.uploadOwnerId,
				session.uploadOwnerClaimId,
				false
			);
			assertUploadSessionCasApplied(applied, "bytes-gone cleanup", session);
		},
		async markNeedsAttention(session, reason, failure?: unknown) {
			// LSP-17823: a retried needs-attention transition keeps its FIRST reason (resolveNextLastError's
			// idempotent-return semantics) — mirror that here so the in-memory snapshot never claims a
			// reason the row didn't actually persist.
			const alreadyNeedsAttention = session.status === "needs-attention";
			// ls-heroku-ci review: log the discard explicitly when a retry's new reason is about to be
			// dropped, mirroring async's "already recorded; discarding newly supplied reason" warn —
			// otherwise the unconditional warnNeedsAttentionReason below claims a reason was persisted
			// when resolveNextLastError is actually about to keep the first one.
			if (alreadyNeedsAttention && reason !== undefined) {
				try {
					console.warn("[Upload] Needs-attention already recorded; discarding newly supplied reason", {
						app: "admin",
						sessionId: session.id,
						uploadId: session.uploadId,
						existingLastError: session.lastError !== undefined,
						discardedReason: redactUploadLogText(fileUploadDb.truncateUploadFailureReason(reason))
					});
				} catch {
					// A diagnostic failure must not block persistence.
				}
			} else {
				warnNeedsAttentionReason(session, reason);
			}
			// Deliberately does NOT throw on a declined CAS, unlike markTerminalComplete/markBytesGone
			// above — needs-attention is the LEAST authoritative terminal verdict, and a decline here
			// routinely means a MORE authoritative terminal state (complete, or the byte-loss cleanup
			// that removes the row outright) already won a legitimate race. Throwing propagated
			// uncaught through runGuardedEffect (shared/frontend/uploads/src/ownershipGate.ts has no
			// try/catch) and out of resumePersistedMultipartSession, where adminResumeSession rethrows
			// anything that is not an UploadCancelledError — so a durably terminal session reached the
			// drain as a generic retryable failure, the exact bug already fixed on the async side.
			// Mirrors asyncSessionStore.markNeedsAttention (PR #3450 review / LSP-17735).
			// LSP-19384: the connectivity gate on the retry ceiling. A park only counts toward the
			// ceiling when the attempt demonstrably reached the network — a device with no connection
			// fails instantly and would otherwise burn the whole budget in seconds while proving
			// nothing about the upload.
			//
			// The shared driver carries the original failure beside its normalized durable message. That
			// preserves HTTP status evidence here without re-parsing human-readable text.
			//
			// Residual gap, accepted: on a captive portal `navigator.onLine` reads true, so a DNS
			// failure does count as evidenced. The clock half contains it — 10 evidenced failures AND
			// four hours means a portal that persists that long, which is a stuck upload the user
			// should be shown rather than one to keep retrying silently.
			//
			// `!alreadyNeedsAttention`: only a park that actually TRANSITIONS into needs-attention spends
			// a slot. This method is also called for a row that is already parked, and on that path
			// `resolveNextLastError` deliberately keeps the FIRST reason because it is the same park — so
			// counting it again let one failure spend several slots and brought the ceiling forward onto
			// a row that had not failed ten times. The two rules now agree about what "one park" means.
			const evidencedFailure = !alreadyNeedsAttention && !isOfflineUploadFailure(failure ?? reason, isBrowserOffline());
			const outcome = await fileUploadDb.updateFileUploadSessionStatusWithOutcome(
				session.id,
				"needs-attention",
				{
					uploadId: session.uploadId,
					uploadOwnerId: session.uploadOwnerId,
					uploadOwnerClaimId: session.uploadOwnerClaimId
				},
				{ lastError: reason, evidencedFailure }
			);
			if (!outcome.applied) {
				warnNeedsAttentionCasDecline(session, outcome);
				// Cancellation clears owner identity in the same write that records the terminal
				// status, so a stale needs-attention snapshot cannot require identityMatched here.
				if (outcome.currentStatus === "cancelled") {
					Object.assign(session, {
						status: "cancelled",
						uploadOwnerId: undefined,
						uploadOwnerHeartbeatAt: undefined,
						uploadOwnerClaimId: undefined
					});
					return "already-cancelled";
				}
				// The identity CAS passed, so terminal precedence is the only remaining decline reason:
				// report the status that actually WON rather than letting the caller describe a
				// completed session as one that needs attention. "needs-attention" itself cannot decline
				// here (it is the status being written), so the only non-complete terminal winner is
				// "cancelled". Adopt that winner in memory so the facade can route the stale attempt through
				// its existing cancellation cleanup path instead of surfacing needs-attention.
				if (outcome.identityMatched) {
					if (outcome.currentStatus === "complete") return "already-complete";
					return undefined;
				}
				// A different uploadId/owner holds the row now (or the row is gone entirely — admin's
				// byte-loss path DELETES rather than persisting a "bytes-gone" status, so a vanished row
				// is indistinguishable from any other deletion and cannot be reported as
				// "already-bytes-gone"). Nothing was durably marked; surface the same signal the outer
				// lease gate produces so the driver yields instead of claiming a terminal verdict.
				return "skipped-not-owned";
			}
			Object.assign(session, {
				status: "needs-attention",
				lastError: alreadyNeedsAttention ? session.lastError : fileUploadDb.truncateUploadFailureReason(reason),
				uploadOwnerId: undefined,
				uploadOwnerHeartbeatAt: undefined,
				uploadOwnerClaimId: undefined
			});
		},
		async markRecoveryBlocked(session, reason) {
			const outcome = await fileUploadDb.updateFileUploadSessionStatusWithOutcome(
				session.id,
				"recovery-blocked",
				{
					uploadId: session.uploadId,
					uploadOwnerId: session.uploadOwnerId,
					uploadOwnerClaimId: session.uploadOwnerClaimId
				},
				{ lastError: reason }
			);
			if (!outcome.applied) {
				if (outcome.currentStatus === "complete") return "already-complete";
				if (outcome.currentStatus === "cancelled") return "already-cancelled";
				if (outcome.currentStatus === "needs-attention") return "already-needs-attention";
				if (!outcome.identityMatched) return "skipped-not-owned";
				return;
			}
			Object.assign(session, {
				status: "recovery-blocked",
				lastError: fileUploadDb.truncateUploadFailureReason(reason),
				uploadOwnerId: undefined,
				uploadOwnerHeartbeatAt: undefined,
				uploadOwnerClaimId: undefined
			});
		},
		async tombstoneCancelled(session, options?: { retainChunks?: boolean; notifyOtherTabs?: boolean }) {
			const outcome = await fileUploadDb.updateFileUploadSessionStatusWithOutcome(
				session.id,
				"cancelled",
				{
					uploadId: session.uploadId,
					uploadOwnerId: session.uploadOwnerId,
					uploadOwnerClaimId: session.uploadOwnerClaimId
				},
				options
			);
			if (!outcome.applied) {
				fileUploadDb.warnFileUploadSessionStatusCasDecline(session.id, "cancelled", outcome);
				throw new Error(`Upload session ${session.id} no longer owns the persisted upload; cannot cancel`);
			}
			Object.assign(session, {
				status: "cancelled",
				uploadOwnerId: undefined,
				uploadOwnerHeartbeatAt: undefined,
				uploadOwnerClaimId: undefined,
				uploadCancellationGeneration: outcome.uploadCancellationGeneration
			});
		},
		async listChunkMeta(session) {
			return fileUploadDb.getAllChunksMeta(session.id);
		},
		async putChunkMeta(session, chunk, options) {
			const applied = await fileUploadDb.adoptFileUploadChunkMetadataIfOwned(
				chunk,
				{
					uploadOwnerId: session.uploadOwnerId,
					uploadOwnerClaimId: session.uploadOwnerClaimId,
					uploadId: session.uploadId
				},
				options
			);
			return applied ? "applied" : "skipped-not-owned";
		},
		async readChunkData(session, chunkIndex) {
			return (await fileUploadDb.getChunkBuffer(session.id, chunkIndex)) ?? undefined;
		},
		async verifyReinitializable(session) {
			return this.verifyReinitializableWithRetainedFile(session);
		},
		async verifyReinitializableWithRetainedFile(session, retainedFile) {
			if (retainedFile) return retainedFile.size > 0 ? "guaranteed" : "missing";
			if (!session) return "indeterminate";
			try {
				const chunks = await fileUploadDb.getAllChunksMeta(session.id);
				if (chunks.length === 0) {
					warnRetainedBytesMissing(session, { reason: "no-chunk-metadata", presentBufferCount: 0 });
					return "missing";
				}
				// R-1 (owner directive 2026-08-26): an already-uploaded chunk is NOT proof the bytes are
				// gone — admin retains every chunk buffer until completion cleanup, and the whole point of
				// reinitializing after `UPLOAD_LOST` is that all `isUploaded` flags belong to a target that
				// no longer exists. The buffers themselves are the only evidence that counts, so this used
				// to answer "missing" for exactly the sessions R-1 exists to rescue. Byte presence,
				// per-chunk size, and the file-size total below remain the (fail-closed) verdict.
				const integrity = await inspectPersistedChunkIntegrity({
					chunks,
					totalChunks: session.totalChunks,
					readBuffer: (chunkIndex) => fileUploadDb.getChunkBuffer(session.id, chunkIndex),
					fileSize: session.fileSize
				});
				if (integrity.ok) return "guaranteed";
				// Diagnostic-logging rule: this verdict is what routes a session to the terminal
				// bytes-gone discard, and it used to return silently — leaving "which buffers were
				// missing" to be reconstructed from indirect signals on the next incident.
				warnRetainedBytesMissing(session, integrity);
				return "missing";
			} catch (error) {
				try {
					console.warn("[Upload] Could not verify retained bytes", {
						sessionId: session.id,
						uploadId: session.uploadId,
						...uploadFailureLogDetails(error)
					});
				} catch {
					// Diagnostics must not replace the safe indeterminate verdict.
				}
				return "indeterminate";
			}
		},
		async attachTarget(session, target, retainOwner = true) {
			const verification = await this.verifyReinitializable(session);
			if (verification !== "guaranteed") throw new Error(`Cannot attach target to ${session.id}: retained bytes ${verification}`);
			const outcome = await fileUploadDb.reinitializeFileUploadSessionTarget(
				session.id,
				{ uploadId: session.uploadId, uploadOwnerId: session.uploadOwnerId, uploadOwnerClaimId: session.uploadOwnerClaimId },
				targetPersistenceFields(target, session),
				{ retainOwner }
			);
			if (!outcome.applied) {
				return { kind: "skipped-not-owned", session: "session" in outcome ? outcome.session : session };
			}
			Object.assign(session, outcome.session);
			return { kind: "attached", session: outcome.session };
		},

		/**
		 * R-1 ("there is no destroy", owner directive 2026-08-26). Admin used to answer a server
		 * `UPLOAD_LOST` verdict by deleting the intact local session and its chunk buffers and telling
		 * the user to re-upload. It now does what contact-async already does: repoint the SAME durable
		 * row at the freshly minted target the shared driver hands in, clear the per-chunk checkpoints
		 * that described the dead upload, and put the row back in the pending pool for the drain to
		 * re-upload from the buffers that never left IndexedDB.
		 *
		 * Fail-closed on every ambiguity, and never destructive:
		 * - "missing" retained bytes throw a `buffers missing …` message, the exact text the shared
		 *   driver's `recoverLostPersistedSession` matches to reach the honest bytes-gone terminal.
		 * - "indeterminate" (an IndexedDB read we could not complete) throws a plain, retryable error —
		 *   an unverifiable read must never be upgraded into a data-loss verdict.
		 * - a declined CAS returns the row that actually WON, unchanged. The driver detects that its
		 *   minted target never landed (`reinitializedSession.uploadId !== opaqueId(target)`) and rolls
		 *   the orphaned multipart upload back itself.
		 */
		async resetForReinitialization(session, target) {
			const verification = await this.verifyReinitializable(session);
			if (verification === "missing") {
				throw new Error(`buffers missing for ${session.id}: retained bytes could not be verified before reinitialization`);
			}
			if (verification === "indeterminate") {
				throw new Error(`could not verify buffers for reinitialization of ${session.id}`);
			}
			const outcome = await fileUploadDb.reinitializeFileUploadSessionTarget(
				session.id,
				{
					uploadId: session.uploadId,
					uploadOwnerId: session.uploadOwnerId,
					uploadOwnerClaimId: session.uploadOwnerClaimId
				},
				targetPersistenceFields(target, session)
			);
			if (outcome.applied) {
				// Keep the caller's in-memory snapshot honest: this row is no longer owned by this lease,
				// and it no longer points at the dead upload.
				Object.assign(session, outcome.session);
				return outcome.session;
			}
			if (outcome.reason === "session-missing") {
				throw new Error(`file upload session ${session.id} disappeared during reinitialization`);
			}
			if (outcome.reason === "buffers-missing") {
				throw new Error(`buffers missing for ${session.id}: retained chunk buffers vanished during reinitialization`);
			}
			// Every other refusal is a successor claim (or a terminal verdict) that legitimately won the
			// race. `reinitializeFileUploadSessionTarget` already logged which condition refused it; the
			// winning row is returned so the driver can see its target was never attached.
			return outcome.session;
		},
		findByUploadId: async (uploadId) => withSessionDefaults(await fileUploadDb.getFileUploadSessionByUploadId(uploadId)),
		async markChunkUploaded(session, chunkIndex, etag) {
			// The Admin driver invokes this through runGuardedEffect/commitIfOwned. Do not reacquire a
			// writer token here: a fresh token could belong to a later epoch after the PUT caller lost
			// ownership. The row CAS remains a silent no-op for expected claimant/retry races.
			await fileUploadDb.markFileUploadChunkUploaded(
				session.id,
				chunkIndex,
				etag,
				session.uploadOwnerId,
				session.uploadId,
				session.uploadOwnerClaimId
			);
		},
		async markChunkUploadedAlreadyFenced(session, chunkIndex, etag) {
			await this.markChunkUploaded(session, chunkIndex, etag);
		},
		// Plan 3a D7: a chunked-object completion refused with the ordinals it is still owed. Only those
		// chunks go back to pending — the buffers stay, so the ordinary chunk loop re-sends exactly them
		// instead of re-uploading the whole file. CASes on the SAME captured identity markChunkUploaded
		// passes above — uploadId alone survives a successor claim and every terminal
		// verdict, so it cannot tell "still ours to edit" from "already finished elsewhere".
		markChunksNotUploaded: (session, chunkIndexes) =>
			fileUploadDb.resetFileUploadChunkUploads(
				session.id,
				chunkIndexes,
				session.uploadId,
				session.uploadOwnerId,
				session.uploadOwnerClaimId
			),
		async deleteSessionByIdIfUploadIdMatches(sessionId, uploadId, expectedIdentity) {
			// A caller that captured its own claim id CASes against THAT, so a successor claim taken between
			// the caller's verdict and this delete is refused instead of destroyed. Authority read from the
			// row being deleted can never detect that successor.
			const capturedClaimId = expectedIdentity?.uploadOwnerClaimId;
			const capturedOwnerId = expectedIdentity?.uploadOwnerId;
			if (capturedClaimId !== undefined) {
				// break-it round 1: a captured claim can sit beside a CORRUPT captured owner — that is this
				// ticket's premise. `deleteFileUploadSession` refuses a corrupt expected owner outright, so
				// passing it as authority would make the row undismissable on every attempt. Fall back to the
				// uploadId CAS plus `refuseValidOwnerLease`, which still refuses a valid successor lease.
				const capturedOwnerCanAuthorize = capturedOwnerId === undefined || fileUploadDb.isValidFileUploadOwnerId(capturedOwnerId);
				if (!capturedOwnerCanAuthorize) {
					try {
						console.warn(
							"[Upload] lost-upload discard falling back to the uploadId CAS — captured owner identity is unusable",
							{
								sessionId,
								uploadId,
								ownerIdLength: fileUploadDb.getFileUploadOwnerIdLength(capturedOwnerId)
							}
						);
					} catch {
						// A diagnostic failure must not change the discard.
					}
				}
				const deleted = capturedOwnerCanAuthorize
					? await fileUploadDb.deleteFileUploadSession(sessionId, uploadId, capturedOwnerId, capturedClaimId, {
							requireExactOwnerIdentity: true,
							notifyOtherTabs: false
						})
					: await fileUploadDb.deleteFileUploadSession(sessionId, uploadId, undefined, undefined, {
							refuseValidOwnerLease: true,
							notifyOtherTabs: false
						});
				if (!deleted) {
					try {
						console.warn("[Upload] lost-upload discard refused — the caller's captured claim no longer holds the row", {
							sessionId,
							uploadId
						});
					} catch {
						// A diagnostic failure must not change the discard refusal.
					}
				}
				return;
			}
			const session = await fileUploadDb.getFileUploadSessionById(sessionId);
			if (!session || session.uploadId !== uploadId) {
				try {
					console.warn("[Upload] lost-upload discard skipped — session is gone or its uploadId moved", {
						sessionId,
						uploadId,
						actualUploadId: session?.uploadId
					});
				} catch {
					// A diagnostic failure must not change the discard refusal.
				}
				return;
			}
			// LSP-18217: only a VALID foreign owner is a live lease worth protecting. A corrupt owner is
			// not a lease, so it must not shield a server-lost row from cleanup — nothing else can delete
			// that row, so refusing here would leave it undismissable in the upload tray.
			const hasWellFormedOwnerId = fileUploadDb.isValidFileUploadOwnerId(session.uploadOwnerId);
			if (hasWellFormedOwnerId && session.uploadOwnerId !== fileUploadDb.getCurrentFileUploadSessionOwnerId()) {
				try {
					console.warn("[Upload] lost-upload discard skipped — session is leased by another tab", {
						sessionId,
						uploadId
					});
				} catch {
					// A diagnostic failure must not change the discard refusal.
				}
				return;
			}
			// A row whose owner is not a valid lease (ownerless, or corrupt per LSP-18217) is deleted
			// through the ownerless call: `deleteFileUploadSession` refuses a corrupt EXPECTED owner
			// outright, so passing it back would strand the row. `refuseValidOwnerLease` replaces the
			// owner CAS this branch gives up: the read above is not transactional, so a tab that claims
			// a real lease in that window is still refused inside the delete transaction.
			if (!hasWellFormedOwnerId) {
				try {
					console.warn("[Upload] lost-upload discard proceeding on a session with no valid owner lease", {
						sessionId,
						uploadId,
						ownerIdLength: fileUploadDb.getFileUploadOwnerIdLength(session.uploadOwnerId)
					});
				} catch {
					// A diagnostic failure must not change the discard.
				}
			}
			const deleted = await fileUploadDb.deleteFileUploadSession(
				sessionId,
				uploadId,
				hasWellFormedOwnerId ? session.uploadOwnerId : undefined,
				hasWellFormedOwnerId ? session.uploadOwnerClaimId : undefined,
				{
					requireExactOwnerIdentity: hasWellFormedOwnerId,
					refuseValidOwnerLease: !hasWellFormedOwnerId,
					notifyOtherTabs: false
				}
			);
			if (!deleted) {
				// The dismissal looks successful to the caller either way, so a refusal that leaves the row
				// for the resume flow to re-surface has to be visible here.
				try {
					console.warn("[Upload] lost-upload discard refused by the delete CAS — the row survived", { sessionId, uploadId });
				} catch {
					// A diagnostic failure must not change the discard outcome.
				}
			}
		}
	};
}
